import React from 'react'
import { Routes, Route, Outlet, Navigate } from 'react-router-dom'

// contexts
import { useSessionContext } from './contexts/SessionContext'

// common
import Loading from './layout/Loading'
import Layout from './layout/Layout'
import Page404 from './views/404'
import FaqPage from './views/FaqPage'
import PrivacyPolicy from './views/PrivacyPolicy'
import About from './views/About'
import Support from './views/Support'
import ExpiredLink from './views/ExpiredLink'
import HowItWorks from './views/HowItWorks'

// anonymous users routes
import Home from './views/Home'
import UserSignin from './views/UserSignin'
import ResetPassword from './views/ResetPassword'
import CreateAccount from './views/CreateAccount'

// unfinalized users routes
import UserFinalize from './views/UserFinalize'

// logged users routes
// profile area
import ProfileLayout from './layout/ProfileLayout'
import Profile from './views/Profile'
import Membership from './views/Membership'
import UserSetNewPassword from './views/UserSetNewPassword'

// dashboard area
import DashboardLayout from './layout/DashboardLayout'
import DashboardAgami from './views/DashboardAgami'
import DashboardExperiences from './views/DashboardExperiences'
import SharedExperience from './views/SharedExperience'
import { ExperienceDashboard } from './ExperienceDashboard/ExperienceDashboard'
import { ExperienceSubmissions } from './ExperienceSubmissions/ExperienceSubmissions'

// upload area
import Upload from './views/Upload'
import UploadAgamiV2 from './ViewUploadV2/UploadAgamiV2'
import AgamiDetailV2 from './ViewUploadV2/AgamiDetailV2'
import { LegacyAgamiDetailRoute, LegacyAgamiUploadRoute } from './ViewUploadV2/AgamiRouteGuards'
import { RedirectAfterSignin, RedirectToSignin } from './components/SigninRedirects'
import UploadExperience from './ViewUpload/UploadExperience'

// admin area
import RequireAdmin from './layout/RequireAdmin'
import AdminPanel from './views/AdminPanel'
import InviteNewUser from './ViewAdmin/InviteNewUser'
import GenerateCode from './ViewAdmin/GenerateCode'
import CodesList from './ViewAdmin/CodesList'
import UserList from './ViewAdmin/UserList'
import ExperienceList from './ViewAdmin/ExperienceList'
import SpotsList from './ViewAdmin/SpotsList'

// experience area
import Experience from './views/Experience'
import { MapControlsContextProvider } from './contexts/MapControlsContext'
import { XpContextProvider } from './contexts/XpContext'
import DashboardAccessCodes from './views/DashboardAccessCodes'
import DashboardPatterns from './views/DashboardPatterns'
import DashboardPatternDetail from './views/DashboardPatternDetail'
import DashboardAgamiIndoor from './views/DashboardAgamiIndoor'
import DashboardLandingPages from './views/DashboardLandingPages'

const AppRouting: React.FC = () => {
  const { user, loading } = useSessionContext()

  if (loading) {
    return <Loading />
  }

  if (process.env.REACT_APP_COMING_SOON_PREVIEW) {
    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  if (!user) {
    // unauth routes
    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="signin" element={<UserSignin />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="register" element={<CreateAccount />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="about" element={<About />} />
          <Route path="support" element={<Support />} />
          <Route path="expired-invitation" element={<ExpiredLink />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          {/* Pages that need an account: sign in first, then land where the
              link pointed (e.g. a proposal email). Keep in step with the
              logged-in routes below; anything else stays a 404. */}
          <Route path="dashboard" element={<RedirectToSignin />} />
          <Route path="experiences/*" element={<RedirectToSignin />} />
          <Route path="shared-experiences/*" element={<RedirectToSignin />} />
          <Route path="spot/*" element={<RedirectToSignin />} />
          <Route path="access-codes" element={<RedirectToSignin />} />
          <Route path="patterns/*" element={<RedirectToSignin />} />
          <Route path="agami-indoor" element={<RedirectToSignin />} />
          <Route path="landing-pages" element={<RedirectToSignin />} />
          <Route path="profile/*" element={<RedirectToSignin />} />
          <Route path="finalize" element={<RedirectToSignin />} />
          <Route path="experience/*" element={<RedirectToSignin />} />
          <Route path="upload/*" element={<RedirectToSignin />} />
          <Route path="admin/*" element={<RedirectToSignin />} />
        </Route>

        <Route path="*" element={<Page404 />} />
      </Routes>
    )
  }

  if (!user.finalized) {
    // user has to complete profile info
    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<UserFinalize />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="about" element={<About />} />
          <Route path="support" element={<Support />} />
          <Route path="expired-invitation" element={<ExpiredLink />} />
          <Route path="how-it-works" element={<HowItWorks />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // normal auth routes
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        {/* LOGGED USERS ROUTES */}
        <Route path="/" element={<DashboardLayout />}>
          <Route index element={<DashboardAgami />} />
          <Route path="experiences" element={<DashboardExperiences />} />
          <Route path="experiences/:experienceId" element={<ExperienceDashboard />} />
          <Route path="experiences/:experienceId/submissions" element={<ExperienceSubmissions />} />
          <Route path="shared-experiences/:experienceId" element={<SharedExperience />} />
          <Route path="spot/:id" element={<LegacyAgamiDetailRoute />} />
          {/* The v2 detail (06.1): the flag guard lives in the target view, so
              without `onde` this lands back on /spot/:id. 06.4 wires the entry
              points; the static segment outranks :id, no ordering needed. */}
          <Route path="spot/v2/:id" element={<AgamiDetailV2 />} />
          <Route path="access-codes" element={<DashboardAccessCodes />} />
          <Route path="patterns" element={<DashboardPatterns />} />
          <Route path="patterns/:patternId" element={<DashboardPatternDetail />} />
          <Route path="agami-indoor" element={<DashboardAgamiIndoor />} />
          <Route path="landing-pages" element={<DashboardLandingPages />} />
        </Route>
        <Route path="profile" element={<ProfileLayout />}>
          <Route index element={<Profile />} />
          <Route path="new-password" element={<UserSetNewPassword />} />
          <Route path="membership" element={<Membership />} />
        </Route>
        <Route path="finalize" element={<UserFinalize />} />
        <Route
          path="experience/:experienceId?"
          element={
            <XpContextProvider>
              <MapControlsContextProvider>
                <Experience />
              </MapControlsContextProvider>
            </XpContextProvider>
          }
        />
        <Route path="upload" element={<Outlet />}>
          <Route index element={<Upload />} />
          <Route path="spot" element={<LegacyAgamiUploadRoute />} />
          <Route path="v2" element={<UploadAgamiV2 />} />
          <Route path="experience" element={<UploadExperience />} />
        </Route>

        {/* ADMIN ROUTES */}
        <Route
          path="admin"
          element={
            <RequireAdmin>
              <Outlet />
            </RequireAdmin>
          }
        >
          <Route index element={<AdminPanel />} />
          <Route path="new-user" element={<InviteNewUser />} />
          <Route path="new-code" element={<GenerateCode />} />
          <Route path="list-codes" element={<CodesList />} />
          <Route path="list-users" element={<UserList />} />
          <Route path="list-experiences" element={<ExperienceList />} />
          <Route path="list-spots" element={<SpotsList />} />
        </Route>

        {/* COMMON ROUTES */}
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="about" element={<About />} />
        <Route path="support" element={<Support />} />
        <Route path="expired-invitation" element={<ExpiredLink />} />
        <Route path="how-it-works" element={<HowItWorks />} />
      </Route>

      <Route path="/dashboard" element={<Navigate to="/" replace />} />

      {/* REDIRECTS */}
      <Route path="signin" element={<RedirectAfterSignin />} />
      <Route path="register" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Page404 />} />
    </Routes>
  )
}

export default AppRouting
